import React, { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Настройки СКУД (Orion). Кастомная панель вместо универсальной SettingsForm —
 * потому что нужен селект подразделений из живого кэша (skud_departments) и
 * кнопка «Проверить подключение» рядом с «Сохранить».
 *
 * Группы полей:
 *   1. Синхронизация — sync_enabled (вкл/выкл всего).
 *   2. Подключение к MSSQL — общий хост, порт MSSQL, инстанс, креды, БД.
 *   3. JSON-RPC — порт SOAP/JSON-RPC, креды soap-токена.
 *   4. Маппинг подразделений — какие подразделения СКУД соответствуют
 *      сотрудникам / ученикам / родителям CRM. Селект подгружается из
 *      /api/skud/departments; пока пусто — fallback на ввод skud_id вручную.
 *
 * До первой синхронизации /api/skud/departments вернёт пустой массив —
 * показываем плашку «выполните первую синхронизацию» и input для skud_id.
 */
export default function SkudSettingsPanel() {
  const can = useCan();
  const canRead  = can('settings.skud', 'read');
  const canWrite = can('settings.skud', 'write');
  const canSync  = can('skud.sync', 'trigger');

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [values,  setValues]  = useState({});
  const [secrets, setSecrets] = useState({});
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, mssql:{ok,message}, rpc:{ok,message}, duration_sec }
  const [syncing, setSyncing] = useState(false);      // true пока висит «running» в логе
  const [lastPull, setLastPull] = useState(null);     // последняя запись skud_sync_log (type=pull)
  const [pullMsg,  setPullMsg]  = useState(null);     // сообщение от кнопки «Синхронизировать сейчас»
  const [eventsState, setEventsState] = useState(null); // { state: {...}, events_in_cache: N }
  const [eventsMsg, setEventsMsg] = useState(null);
  const [outboundState, setOutboundState] = useState(null); // { pending: {...}, total, worker: {...} }
  const [outboundMsg, setOutboundMsg] = useState(null);

  // Загрузка настроек + подразделений + последнего pull-лога.
  // Лог поллим, пока статус = running.
  const reloadLastPull = async () => {
    try {
      const r = await api.get('/api/skud/sync-log?type=pull&limit=1');
      setLastPull(r.rows?.[0] || null);
      if (r.rows?.[0]?.status !== 'running') setSyncing(false);
    } catch { /* тихо */ }
  };

  const reloadEventsState = async () => {
    try {
      const r = await api.get('/api/skud/events-state');
      setEventsState(r || null);
    } catch { /* тихо */ }
  };

  const reloadOutboundState = async () => {
    try {
      const r = await api.get('/api/skud/outbound-state');
      setOutboundState(r || null);
    } catch { /* тихо */ }
  };

  useEffect(() => {
    if (!canRead) { setLoading(false); return; }
    let aborted = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/api/settings/skud'),
      api.get('/api/skud/departments').catch(() => ({ rows: [] })),
      api.get('/api/skud/sync-log?type=pull&limit=1').catch(() => ({ rows: [] })),
      api.get('/api/skud/events-state').catch(() => null),
      api.get('/api/skud/outbound-state').catch(() => null),
    ]).then(([s, d, l, ev, ob]) => {
      if (aborted) return;
      setValues(s.value || {});
      setSecrets(s.secrets || {});
      setDepartments(d.rows || []);
      const lp = l.rows?.[0] || null;
      setLastPull(lp);
      if (lp?.status === 'running') setSyncing(true);
      setEventsState(ev);
      setOutboundState(ob);
    }).catch((e) => {
      if (aborted) return;
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить настройки СКУД');
    }).finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [canRead]);

  // Поллинг лога, пока идёт pull.
  useEffect(() => {
    if (!syncing) return undefined;
    const id = setInterval(reloadLastPull, 2000);
    return () => clearInterval(id);
  }, [syncing]);

  // Поллинг состояния событий: в idle — раз в 30 секунд (мониторим, не упало
  // ли), в running/backfill — раз в 3 секунды (показать прогресс).
  useEffect(() => {
    if (!canRead) return undefined;
    const status = eventsState?.state?.status;
    const fast = status === 'running' || status === 'backfill';
    const id = setInterval(reloadEventsState, fast ? 3000 : 30000);
    return () => clearInterval(id);
  }, [canRead, eventsState?.state?.status]);

  // Поллинг состояния outbound: если есть pending — раз в 3 сек, иначе — 30.
  useEffect(() => {
    if (!canRead) return undefined;
    const hasPending = (outboundState?.total || 0) > 0;
    const id = setInterval(reloadOutboundState, hasPending ? 3000 : 30000);
    return () => clearInterval(id);
  }, [canRead, outboundState?.total]);

  const set = (name, val) => {
    setValues((v) => ({ ...v, [name]: val }));
    setSavedAt(null);
    setTestResult(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.put('/api/settings/skud', { value: values });
      setValues(res.value || {});
      setSecrets(res.secrets || {});
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!canWrite) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const res = await api.post('/api/skud/test', {});
      setTestResult(res);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось выполнить проверку';
      setTestResult({ ok: false, mssql: { ok: false, message: msg }, rpc: { ok: false, message: '' } });
    } finally {
      setTesting(false);
    }
  };

  const onSyncNow = async () => {
    if (!canSync || syncing) return;
    setPullMsg(null);
    setSyncing(true);
    try {
      await api.post('/api/skud/sync', {});
      setPullMsg({ kind: 'ok', text: 'Синхронизация запущена. Прогресс смотрите ниже.' });
      // Сразу дёрнем лог, чтобы появился running-статус.
      reloadLastPull();
    } catch (err) {
      setSyncing(false);
      const msg = err instanceof ApiError ? err.message : 'Не удалось запустить синхронизацию';
      setPullMsg({ kind: 'err', text: msg });
    }
  };

  const onEventsSyncNow = async () => {
    if (!canSync) return;
    setEventsMsg(null);
    try {
      await api.post('/api/skud/events/sync', {});
      setEventsMsg({ kind: 'ok', text: 'Тик событий запущен. Прогресс обновляется ниже.' });
      reloadEventsState();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось запустить';
      setEventsMsg({ kind: 'err', text: msg });
    }
  };

  const onOutboundSyncNow = async () => {
    if (!canSync) return;
    setOutboundMsg(null);
    try {
      await api.post('/api/skud/outbound/sync', {});
      setOutboundMsg({ kind: 'ok', text: 'Отправка запущена. Список ниже обновится через пару секунд.' });
      setTimeout(reloadOutboundState, 1500);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось запустить';
      setOutboundMsg({ kind: 'err', text: msg });
    }
  };

  if (!canRead) {
    return <div className="settings-card"><h2>Нет доступа</h2><p>У вас нет права на чтение настроек СКУД.</p></div>;
  }
  if (loading) return <div className="settings-card">Загрузка…</div>;

  return (
    <form className="settings-card" onSubmit={onSubmit}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">Настройка СКУД (Orion)</h2>
        <p className="settings-card__sub">
          Параметры подключения к Orion: MSSQL (источник кэша и журнала событий)
          и JSON-RPC (управление людьми / пропусками / открытием дверей).
          Маппинг подразделений нужен, чтобы при архивации ученика автоматически
          блокировать его пропуска.
        </p>
      </div>

      {/* ── 1. Включатель ───────────────────────────────────────── */}
      <Fieldset title="Синхронизация" first>
        <Field label="Синхронизация" wide>
          <select
            className="settings-field__input"
            value={values.sync_enabled || 'off'}
            disabled={!canWrite}
            onChange={(e) => set('sync_enabled', e.target.value)}
          >
            <option value="off">Выключена</option>
            <option value="on">Включена</option>
          </select>
          <Hint>
            Когда выключена — все таймеры (pull 30 мин, события 10 с, отправка
            в СКУД 10 с) пропускают тики. Подходит для отладки и инцидентов.
          </Hint>
        </Field>
      </Fieldset>

      {/* ── 2. MSSQL ───────────────────────────────────────────── */}
      <Fieldset title="Подключение к MSSQL (Orion)">
        <Field label="Хост">
          <input className="settings-field__input" value={values.host || ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('host', e.target.value)}
                 placeholder="orion.ng.local" />
          <Hint>Один общий хост для MSSQL и JSON-RPC.</Hint>
        </Field>
        <Field label="Порт MSSQL">
          <input className="settings-field__input" type="number" min="1" max="65535"
                 value={values.mssql_port ?? ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('mssql_port', e.target.value === '' ? null : Number(e.target.value))}
                 placeholder="1433" />
        </Field>
        <Field label="Инстанс">
          <input className="settings-field__input" value={values.mssql_instance || ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('mssql_instance', e.target.value)}
                 placeholder="MSSQLSERVER" />
          <Hint>Для дефолтного инстанса — MSSQLSERVER. Для именованных — например, SQLEXPRESS.</Hint>
        </Field>
        <Field label="Логин">
          <input className="settings-field__input" value={values.mssql_user || ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('mssql_user', e.target.value)}
                 placeholder="crm" autoComplete="off" />
        </Field>
        <Field label="Пароль">
          <SecretInput
            name="mssql_password"
            value={values.mssql_password || ''}
            hasStored={!!secrets.mssql_password}
            canWrite={canWrite}
            onChange={(v) => set('mssql_password', v)}
          />
        </Field>
        <Field label="База">
          <input className="settings-field__input" value={values.mssql_database || ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('mssql_database', e.target.value)}
                 placeholder="Orion" />
        </Field>
      </Fieldset>

      {/* ── 3. JSON-RPC ────────────────────────────────────────── */}
      <Fieldset title="JSON-RPC (управление СКУД)">
        <Field label="Порт SOAP/JSON-RPC">
          <input className="settings-field__input" type="number" min="1" max="65535"
                 value={values.soap_port ?? ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('soap_port', e.target.value === '' ? null : Number(e.target.value))}
                 placeholder="8090" />
          <Hint>Эндпоинт: http://&lt;хост&gt;:&lt;порт&gt;/jsonrpc/iorionpro</Hint>
        </Field>
        <Field label="Логин soap">
          <input className="settings-field__input" value={values.soap_user || ''}
                 readOnly={!canWrite}
                 onChange={(e) => set('soap_user', e.target.value)}
                 autoComplete="off" />
          <Hint>Используется для getLoginToken — нужен только для команд управления (например, открыть точку доступа).</Hint>
        </Field>
        <Field label="Пароль soap">
          <SecretInput
            name="soap_password"
            value={values.soap_password || ''}
            hasStored={!!secrets.soap_password}
            canWrite={canWrite}
            onChange={(v) => set('soap_password', v)}
          />
          <Hint>Хранится в БД как есть; в MD5 хешируется на лету при вызове Orion.</Hint>
        </Field>
      </Fieldset>

      {/* ── 4. Маппинг подразделений ───────────────────────────── */}
      <Fieldset title="Маппинг подразделений">
        {departments.length === 0 ? (
          <div className="settings-field settings-field--wide">
            <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginBottom: 8 }}>
              Список подразделений будет загружен после первой успешной синхронизации.
              Пока введите skud_id вручную, если вы их знаете.
            </div>
          </div>
        ) : null}
        <DepartmentField
          label="Сотрудники"
          value={values.department_employees_skud_id}
          departments={departments}
          canWrite={canWrite}
          onChange={(v) => set('department_employees_skud_id', v)}
          hint="Подразделение для учителей, администраторов и прочих сотрудников."
        />
        <DepartmentField
          label="Ученики"
          value={values.department_students_skud_id}
          departments={departments}
          canWrite={canWrite}
          onChange={(v) => set('department_students_skud_id', v)}
          hint="Подразделение для школьников и детсадовцев."
        />
        <DepartmentField
          label="Родители"
          value={values.department_parents_skud_id}
          departments={departments}
          canWrite={canWrite}
          onChange={(v) => set('department_parents_skud_id', v)}
          hint="Подразделение для родителей (отдельные пропуска)."
        />
      </Fieldset>

      {/* ── Синхронизация (кнопка + статус последнего pull) ─────── */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Полная синхронизация (pull)</div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--sm"
            onClick={onSyncNow}
            disabled={!canSync || syncing}
            title={canSync ? 'Запустить полный pull из MSSQL и getKeys' : 'Нет права skud.sync/trigger'}
          >
            {syncing ? 'Идёт синхронизация…' : 'Синхронизировать сейчас'}
          </button>
        </div>
        {pullMsg && (
          <div style={{
            fontSize: 12.5,
            color: pullMsg.kind === 'ok' ? 'var(--green-700)' : 'var(--red-fg)',
            marginBottom: 8,
          }}>
            {pullMsg.text}
          </div>
        )}
        <LastPullBlock row={lastPull} />
      </div>

      {/* ── Журнал событий (pLogData → skud_events) ────────────── */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Журнал событий (pLogData → skud_events)</div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--sm"
            onClick={onEventsSyncNow}
            disabled={!canSync || eventsState?.state?.status === 'running' || eventsState?.state?.status === 'backfill'}
            title={canSync ? 'Пинок: «не жди следующих 10 секунд, синкни сейчас»' : 'Нет права skud.sync/trigger'}
          >
            Синкнуть события сейчас
          </button>
        </div>
        {eventsMsg && (
          <div style={{
            fontSize: 12.5,
            color: eventsMsg.kind === 'ok' ? 'var(--green-700)' : 'var(--red-fg)',
            marginBottom: 8,
          }}>
            {eventsMsg.text}
          </div>
        )}
        <EventsStateBlock state={eventsState} />
      </div>

      {/* ── Отправка изменений в СКУД (outbound) ────────────────── */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Отправка в СКУД (outbound)</div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn--sm"
            onClick={onOutboundSyncNow}
            disabled={!canSync || (outboundState?.total || 0) === 0}
            title={canSync ? 'Не ждать следующих 10 секунд, отправить сейчас' : 'Нет права skud.sync/trigger'}
          >
            Отправить сейчас
          </button>
        </div>
        {outboundMsg && (
          <div style={{
            fontSize: 12.5,
            color: outboundMsg.kind === 'ok' ? 'var(--green-700)' : 'var(--red-fg)',
            marginBottom: 8,
          }}>
            {outboundMsg.text}
          </div>
        )}
        <OutboundStateBlock state={outboundState} />
      </div>

      {/* ── Результат теста подключения ────────────────────────── */}
      {testResult && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 12,
            background: testResult.ok ? 'var(--green-50, #f0fff4)' : 'var(--red-50, #fff5f5)',
            fontSize: 13,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {testResult.ok ? '✓ Подключение в порядке' : '✗ Есть проблемы'}
              <span style={{ color: 'var(--fg-muted)', fontWeight: 400, marginLeft: 8 }}>
                ({testResult.duration_sec ?? '?'} с)
              </span>
            </div>
            <ResultBlock title="MSSQL" result={testResult.mssql} />
            <ResultBlock title="JSON-RPC" result={testResult.rpc} />
          </div>
        </div>
      )}

      <div className="settings-card__foot">
        {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5 }}>{error}</div>}
        {savedAt && !error && (
          <div style={{ color: 'var(--green-700)', fontSize: 12.5 }}>
            Сохранено в {savedAt.toLocaleTimeString('ru-RU')}
          </div>
        )}
        <div className="settings-card__spacer" />
        <button
          type="button"
          className="btn btn--sm"
          onClick={onTest}
          disabled={!canWrite || testing || saving}
          title={canWrite ? 'Проверить MSSQL и JSON-RPC' : 'Нет права на запись'}
        >
          {testing ? 'Проверяем…' : 'Проверить подключение'}
        </button>
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!canWrite || saving || testing}
          title={canWrite ? undefined : 'Нет права на запись'}
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}

/* ─── вспомогательные компоненты ──────────────────────────────── */

/**
 * Группа полей с заголовком. Выровнен по горизонтальному паддингу
 * .settings-grid (20px), между группами — тонкий divider.
 * Для самой первой группы передавай `first`, чтобы не дублировать
 * границу с .settings-card__head.
 */
function Fieldset({ title, children, first }) {
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--border)' }}>
      <div style={{
        padding: '14px 20px 0',
        fontWeight: 600,
        fontSize: 13,
        color: 'var(--fg)',
      }}>
        {title}
      </div>
      <div className="settings-grid" style={{ paddingTop: 10 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={'settings-field' + (wide ? ' settings-field--wide' : '')}>
      <label className="settings-field__label">{label}</label>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <div className="settings-field__hint">{children}</div>;
}

function SecretInput({ name, value, hasStored, canWrite, onChange }) {
  return (
    <>
      <input
        className="settings-field__input"
        type="password"
        value={value || ''}
        readOnly={!canWrite}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        placeholder={hasStored ? '•••••• (оставьте пустым, чтобы не менять)' : ''}
      />
      {hasStored && canWrite && (
        <div className="settings-field__hint">Значение задано. Введите новое или оставьте пустым.</div>
      )}
    </>
  );
}

function DepartmentField({ label, value, departments, canWrite, onChange, hint }) {
  const v = value == null ? '' : String(value);
  if (departments.length > 0) {
    return (
      <Field label={label}>
        <select
          className="settings-field__input"
          value={v}
          disabled={!canWrite}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">— не задано —</option>
          {departments.map((d) => (
            <option key={d.skud_id} value={d.skud_id}>
              {d.name} (#{d.skud_id})
            </option>
          ))}
        </select>
        {hint && <Hint>{hint}</Hint>}
      </Field>
    );
  }
  // Fallback — ввод skud_id вручную, пока список не подгрузился
  return (
    <Field label={label}>
      <input
        className="settings-field__input"
        type="number"
        min="1"
        value={v}
        readOnly={!canWrite}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="skud_id"
      />
      {hint && <Hint>{hint}</Hint>}
    </Field>
  );
}

function LastPullBlock({ row }) {
  if (!row) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
        Синхронизация ещё не запускалась.
      </div>
    );
  }

  const fmt = (s) => {
    if (!s) return '—';
    try {
      return new Date(String(s).replace(' ', 'T') + 'Z')
        .toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit',
                                   day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return s; }
  };

  const statusColor =
    row.status === 'ok'      ? 'var(--green-700)' :
    row.status === 'error'   ? 'var(--red-fg)' :
    row.status === 'running' ? 'var(--fg-muted)' : 'var(--fg)';

  const counts = [
    ['Компании',     row.companies_synced],
    ['Подразделения', row.departments_synced],
    ['Люди',         row.persons_synced],
    ['Фото',         row.photos_synced],
    ['Точки доступа', row.accesspoints_synced],
    ['Группы',       row.groups_synced],
    ['Виды событий', row.event_types_synced],
    ['Пропуска',     row.passes_synced],
  ].filter(([_, v]) => v != null);

  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
      <div>
        <span style={{ color: statusColor, fontWeight: 600, marginRight: 8 }}>
          {row.status === 'ok'      ? '✓ Успешно'   :
           row.status === 'error'   ? '✗ Ошибка'    :
           row.status === 'running' ? '⟳ В процессе…' : row.status}
        </span>
        <span style={{ color: 'var(--fg-muted)' }}>
          {fmt(row.finished_at || row.started_at)}
          {row.duration_sec != null && ` · ${row.duration_sec} с`}
          {row.trigger && ` · ${row.trigger === 'manual' ? 'вручную' : row.trigger === 'schedule' ? 'по расписанию' : row.trigger}`}
        </span>
      </div>
      {counts.length > 0 && row.status !== 'error' && (
        <div style={{ marginTop: 4, color: 'var(--fg)' }}>
          {counts.map(([label, v]) => `${label}: ${v}`).join(' · ')}
        </div>
      )}
      {row.error_message && (
        <pre style={{
          margin: '4px 0 0', whiteSpace: 'pre-wrap',
          fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
          color: 'var(--red-fg)',
        }}>
          {row.error_message}
        </pre>
      )}
    </div>
  );
}

/**
 * Показывает текущее состояние воркера событий: статус, накоплено всего
 * в локальном кэше, момент последнего тика. В backfill — пишем «идёт
 * backfill», в idle — «нагнал хвост». При ошибке — текст ошибки.
 */
function EventsStateBlock({ state }) {
  if (!state) {
    return <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Загрузка состояния…</div>;
  }
  const s = state.state || {};
  const total = state.events_in_cache ?? 0;

  const STATUS_LABEL = {
    idle:     { label: '✓ Догнал', color: 'var(--green-700)' },
    backfill: { label: '⟳ Backfill (тянем историю)', color: 'var(--fg-muted)' },
    running:  { label: '⟳ Тик идёт',     color: 'var(--fg-muted)' },
    error:    { label: '✗ Ошибка',       color: 'var(--red-fg)' },
    paused:   { label: '⏸ На паузе',     color: 'var(--fg-muted)' },
  };
  const meta = STATUS_LABEL[s.status] || { label: s.status || '—', color: 'var(--fg)' };

  const fmt = (v) => {
    if (!v) return '—';
    try {
      return new Date(String(v).replace(' ', 'T') + 'Z')
        .toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit',
                                   day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return v; }
  };

  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
      <div>
        <span style={{ color: meta.color, fontWeight: 600, marginRight: 8 }}>{meta.label}</span>
        <span style={{ color: 'var(--fg-muted)' }}>
          {`Накоплено в кэше: ${total.toLocaleString('ru-RU')}`}
          {s.rows_total != null && ` · с начала наблюдений: ${Number(s.rows_total).toLocaleString('ru-RU')}`}
        </span>
      </div>
      <div style={{ color: 'var(--fg-muted)' }}>
        Последний тик: {fmt(s.last_run_at)}
        {s.last_success_at && ` · успешно: ${fmt(s.last_success_at)}`}
        {s.last_time_val && ` · курсор: ${fmt(s.last_time_val)}`}
      </div>
      {s.last_error && (
        <pre style={{
          margin: '4px 0 0', whiteSpace: 'pre-wrap',
          fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
          color: 'var(--red-fg)',
        }}>
          {s.last_error}
        </pre>
      )}
    </div>
  );
}

/**
 * Состояние outbound-воркера: что в очереди + статус (paused/running).
 */
function OutboundStateBlock({ state }) {
  if (!state) return <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Загрузка…</div>;
  const total = state.total || 0;
  const w = state.worker || null;

  if (total === 0 && (!w || (!w.paused && w.consecutive_errors === 0))) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--green-700)' }}>
        ✓ Нечего отправлять — все локальные изменения отправлены в СКУД.
      </div>
    );
  }

  const LABELS = [
    ['companies',   'Компании'],
    ['departments', 'Подразделения'],
    ['persons',     'Люди'],
    ['passes_add',  'Пропуска (создание/изменение)'],
    ['passes_del',  'Пропуска (удаление)'],
    ['gracc_add',   'Доступы (добавление)'],
    ['gracc_del',   'Доступы (удаление)'],
  ];
  const items = LABELS
    .filter(([k]) => (state.pending?.[k] || 0) > 0)
    .map(([k, label]) => `${label}: ${state.pending[k]}`);

  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
      {total > 0 ? (
        <div>
          <span style={{ fontWeight: 600, marginRight: 8 }}>
            Ожидают отправки: {total.toLocaleString('ru-RU')}
          </span>
          {items.length > 0 && (
            <span style={{ color: 'var(--fg-muted)' }}>· {items.join(' · ')}</span>
          )}
        </div>
      ) : (
        <div style={{ color: 'var(--green-700)' }}>✓ Очередь пуста.</div>
      )}
      {w?.paused && (
        <div style={{ color: 'var(--red-fg)', marginTop: 4 }}>
          ⏸ Воркер на паузе ({w.consecutive_errors} ошибок подряд).
          {w.skip_until && ` Возобновится в ${new Date(w.skip_until).toLocaleTimeString('ru-RU')}.`}
        </div>
      )}
      {!w?.paused && w?.consecutive_errors > 0 && (
        <div style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
          Недавние ошибки: {w.consecutive_errors} (порог паузы — 3).
        </div>
      )}
    </div>
  );
}

function ResultBlock({ title, result }) {
  const ok = !!result?.ok;
  return (
    <div style={{ marginTop: 4 }}>
      <span style={{ fontWeight: 500, marginRight: 6 }}>{ok ? '✓' : '✗'} {title}:</span>
      <pre style={{
        display: 'inline-block',
        margin: 0,
        padding: 0,
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        color: ok ? 'var(--fg)' : 'var(--red-fg)',
      }}>
        {result?.message || (ok ? 'OK' : 'ошибка')}
      </pre>
    </div>
  );
}
